import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface Props {
  accept: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles(files: File[]): void;
  className?: string;
  children: React.ReactNode;
  "aria-label"?: string;
}

// A click-or-drop target for files. It renders a button, so it works from the keyboard.
const FileDropzone: React.FC<Props> = ({
  accept,
  multiple = false,
  disabled = false,
  onFiles,
  className,
  children,
  ...props
}) => {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const take = (list: FileList | null) => {
    const files = Array.from(list ?? []);
    if (files.length > 0) onFiles(multiple ? files : files.slice(0, 1));
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        aria-label={props["aria-label"]}
        onClick={() => input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (!disabled) take(e.dataTransfer.files);
        }}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 text-center text-sm text-muted-foreground transition-colors outline-none hover:border-primary/50 hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60",
          over && "border-primary bg-primary/5",
          className,
        )}
      >
        {children}
      </button>
      <input
        ref={input}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        tabIndex={-1}
        onChange={(e) => {
          take(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );
};

export default FileDropzone;
